import _ from 'lodash';
import moment from 'moment';
import numeral from 'numeral';
import React from 'react';
import { connect } from 'react-redux';

import ToastAx              from 'app/actions/toast';
import BackstageApi         from 'app/apis/backstage';
import BtnMasqueradeCompany from 'app/components/backstage/common/btn-masquerade-company';
import BalancesByOwner      from 'app/components/backstage/common/balances-by-owner';
import BackstageLayout      from 'app/components/backstage/layout';
import ModalEdit            from 'app/components/backstage/modal-edit-company';
import Icon                 from 'app/components/common/icon';
import Link                 from 'app/components/common/link';
import StandardInput        from 'app/components/common/standard-input';
import config               from 'app/config';
import {
  Features,
  CompanyCustomerTypes as CustomerTypes,
  CompanyCustomerTypeNames as TypeNames,
  CompanyCustomerStatusNames as StatusNames,
}                           from 'app/constants';
import paths                from 'app/paths';
import prompts              from 'app/prompts';
import RoutingSlx           from 'app/selectors/routing';
import backstage from 'server/serializers/backstage';

class BackstageCompanyPage extends React.PureComponent {

  constructor(props) {
    super(props);

    this.state = {
      company: null,
      showEditModal: false,
      isTerminating: false,
      isDeactivatingEmps: false,
      domains: null,
      newDomain: '',
      newDomainPending: false,
    };

    this.onClickSendInvites = this.onClickSendInvites.bind(this);
    this.onClickTerminate = this.onClickTerminate.bind(this);
    this.onClickDeactivateEmps = this.onClickDeactivateEmps.bind(this);
    this.onClickDtdSend = this.onClickDtdSend.bind(this);
    this.onClickEdit = this.onClickEdit.bind(this);
    this.onCloseEditModal = this.onCloseEditModal.bind(this);
    this.onChangeNewDomain = this.onChangeNewDomain.bind(this);
    this.onClickAddDomain = this.onClickAddDomain.bind(this);
  }

  onClickDtdSend() {
    BackstageApi.companiesSendToDtd(this.props.qsId).then(() => {
      this.fetch();
    });
  }

  onClickEdit() {
    this.setState({showEditModal: true});
  }

  onCloseEditModal() {
    this.setState({showEditModal: false});
  }

  async onClickDeleteDomain(domain) {
    console.log('delete', domain);
    const didConfirm = await prompts.confirm({msg: 'You sure?'});
    if (didConfirm) {
      BackstageApi.companiesDomainsDelete(this.props.qsId, domain).then(({domains}) => {
        this.setState({domains});
      });
    }
  }
  onChangeNewDomain(event) {
    const newDomain = event.target.value;
    this.setState({newDomain});
  }
  async onClickAddDomain() {
    const domain = (this.state.newDomain || '').trim().toLowerCase();
    const msg = `Add ${domain} ?`;
    const didConfirm = await prompts.confirm({msg});
    if (didConfirm) {
      this.setState({newDomainPending: true});
      BackstageApi.companiesDomainsCreate(this.props.qsId, domain).then(({domains}) => {
        this.setState({domains, newDomainPending: false, newDomain: ''});
      }).catch((error) => {
        this.setState({newDomainPending: false});
        this.props.toastError('Oops! Something went wrong. See dev console.');
        console.error(error);
      });
    }
  }

  async onClickSendInvites() {
    const msg = `This will send invite emails to pending employees of the company.\nEmails are usually received momentarily, but sometimes delayed by around 30min.\nAre you sure?`;
    const didConfirm = await prompts.confirm({msg});
    if (!didConfirm) return;
    try {
      const resp = await BackstageApi.companiesSendInvites(this.state.company.id);
      this.props.toastSuccess(`Invites are on the way to ${resp.invitedEmployeeCount} employees!`);
    } catch (error) {
      console.error(error);
      this.props.toastError('Oops! Something went wrong. Error logged to console.');
    }
  }

  async onClickTerminate() {
    const msg = `This will completely shutdown ${this.state.company.name} on Millie. Many settings will be completely deleted, which can not be undone.\nThis will:\n- delete all payment methods\n- disconnect integrations (slack, sso, etc)\n- disable all program features\n- nullify many settings\n- zero-out all budgets\n\nNote that deactivating employees is a separate process.`;
    const didConfirm = await prompts.confirm({msg, doubleConfirm: true, danger: `Terminate ${this.state.company.name}`});
    if (!didConfirm) return;
    this.setState({isTerminating: true});
    try {
      const resp = await BackstageApi.companiesTerminate(this.state.company.id);
      this.props.toastSuccess(`Company terminated. Don't forget to deactivate employees.`);
    } catch (error) {
      console.error(error);
      this.props.toastError('Ruh roh! Something went wrong. Error logged to console. Please notify Tyler.');
    }
    this.setState({isTerminating: false});
  }

  async onClickDeactivateEmps() {
    const msg = `Are you sure you want to deactivate all employees of ${this.state.company.name}?`;
    const didConfirm = await prompts.confirm({msg, doubleConfirm: true, danger: `Deactivate all ${this.state.company.name} employees`});
    if (!didConfirm) return;
    this.setState({isDeactivatingEmps: true});
    try {
      const resp = await BackstageApi.companiesDeactivateEmployees(this.state.company.id);
      this.props.toastSuccess(`Done. Don't forget to "terminate" the company as well.`);
    } catch (error) {
      console.error(error);
      this.props.toastError('Ruh roh! Something went wrong. Error logged to console. Please notify Tyler.');
    }
    this.setState({isDeactivatingEmps: false});
  }

  /*
   *  Life Cycle
   */

  componentDidMount() {
    this.fetch();
  }

  componentDidUpdate(prevProps) {
    if ((prevProps.qsId !== this.props.qsId) && this.props.qsId) {
      this.fetch();
    }
  }

  /*
   *  Tasks
   */

  fetch() {
    this.setState({company: null});
    BackstageApi.companiesGet(this.props.qsId).then(({company}) => {
      this.setState({company});
    });
    this.fetchDomains();
  }

  fetchDomains() {
    BackstageApi.companiesDomainsGet(this.props.qsId).then(({domains}) => {
      this.setState({domains});
    });
  }

  /*
   *  Render
   */

  renderKeyVals(keyVals) {
    return (
      <table className="backstage">
        <tbody>
          {_.map(keyVals, (val, key) => (
            <tr key={key}>
              <th>{key}</th>
              <td>{val}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  renderDetails() {
    const { company } = this.state;
    if (!company) return null;
    const { customerGivingUser: cgUser, employeeGivingUser: egUser } = company;

    const keyVals = {
      'ID': company.id,
      'Source': company.source,
      'Launched?': company.isLaunched ? 'YES' : 'NO',
      'Year 2020 Starts On': moment.utc(company.year2020StartDate).format('MMMM DD, YYYY'),
      'Current Year': (<span>
        <strong>{company.currentYear}</strong>
        <br />
        <span>{moment.utc(company.currentYearStartDate).format('MM/DD/YY')} - {moment.utc(company.currentYearEndDate).format('MM/DD/YY')}</span>
      </span>),
      'Timezone': company.timezone,
      'URL Slug': company.slug,
      'Has MS Teams': company.hasTeams ? 'Yes' : 'No',
      'Employees': (<span>
        {numeral(company.employeeCount).format('0,0')} / {numeral(company.saasEmployeeCap).format('0,0')}
      </span>),
      'Customer Giving User': cgUser ? (<span>
        <Link href={paths.user(cgUser.id)}>{`${cgUser.firstName} ${cgUser.lastName}`}</Link>
        <br />
        <span>{cgUser.email}</span>
      </span>) : '',
      'Employee Giving User': egUser ? (<span>
        <Link href={paths.user(egUser.id)}>{`${egUser.firstName} ${egUser.lastName}`}</Link>
        <br />
        <span>{egUser.email}</span>
      </span>) : '',
      'Customer Type': TypeNames[company.customerType] || company.customerType,
      'Customer Status': StatusNames[company.customerStatus] || company.customerStatus,
    };

    return this.renderKeyVals(keyVals);
  }

  renderFeatures() {
    const { company } = this.state;
    if (!company) return null;

    return (
      <div>
        {Object.values(Features).map((feature) => {
          const isOn = company.saasWhitelist.includes(feature);
          return <div key={feature} className={`bs-feature-pill ${isOn ? 'on' : 'off'}`}>{feature}</div>;
        })}
      </div>
    );
  }

  renderDtd() {
    const { company } = this.state;
    if (!company) return null;
    return (
      <div>
        Update Last Sent: {company.dtdUpdatedAt && moment(company.dtdUpdatedAt).format('lll')}
        <br /><br />
        <button className="btn small" onClick={this.onClickDtdSend}>Send Update</button>
      </div>
    );
  }

  renderDomains() {
    const {domains, newDomain, newDomainPending} = this.state;
    if (!domains) return <p>loading...</p>;
    return (
      <div>
        <ul>
          {domains.map((domain) => {
            return (
              <li key={domain}>{domain}&nbsp;<button className="btn xs secondary ghost danger" onClick={this.onClickDeleteDomain.bind(this, domain)}>Delete</button></li>
            );
          })}
        </ul>
        <div className="_flex _align-start _justify-start _gap-6">
          <StandardInput name="domain" value={newDomain || ''} onChange={this.onChangeNewDomain} label="new-domain.com" className="small" />
          <button className="small btn blue" disabled={!newDomain || newDomainPending} onClick={this.onClickAddDomain}>Add Domain</button>
        </div>
      </div>
    );
  }

  render() {
    const { company, showEditModal, isTerminating, isDeactivatingEmps } = this.state;
    const stripeUrl = `${config.stripeDashboardUrl}/customers/${company?.stripeCustomerId}`;
    return (
      <BackstageLayout>
        <div className="bs-page page-bs-company">
          <h1 className="bs-page-h1">Company - {company ? company.name : null}</h1>

          <br />
          <h2>Actions</h2> 
          {company && (<div className="page-bs-company-actions">
            <button className="btn small" onClick={this.onClickEdit}>Edit</button>
            &nbsp;
            <a href={stripeUrl} target="_blank" className="btn small icon"><Icon.ExternalLink />Stripe Profile</a>
            &nbsp;
            <a href={`/api/cadmin/${company.id}/subscription/go-to-portal`} target="_blank" className="btn small icon"><Icon.ExternalLink />Customer Billing Portal</a>
            &nbsp;
            <button className="btn small" onClick={this.onClickSendInvites} disabled={!company?.isLaunched}>Send Employee Invites</button>
            &nbsp;
            <BtnMasqueradeCompany className="small" id={company.id} />
            &nbsp;
            <Link className="btn small danger" href={paths.cadminDashboardView(company.slug, company.companyDashboardId)}>Admin</Link>
          </div>)}

          <br />
          <h2>Details</h2>
          {this.renderDetails()}

          <br />
          <h2>Features</h2>
          {this.renderFeatures()}

          <br />
          <h2>Balances</h2>
          {company && (
            <BalancesByOwner types={[BalancesByOwner.Types.COMPANY]} id={company.id} key={company.id} />
          )}

          <br />
          <h2>Domains</h2>
          {this.renderDomains()}

          <br />
          <h2>Double The Donation</h2>
          {this.renderDtd()}


          <br />
          <h2>Danger Zone 💣</h2>
          {company && (<div className="page-bs-company-actions">
            <button className="btn small danger" disabled={isTerminating || isDeactivatingEmps} onClick={this.onClickDeactivateEmps}>{isDeactivatingEmps ? 'Deactivating...' : 'Deactivate Employees'}</button>
            &nbsp;
            <button className="btn small danger" disabled={isTerminating || isDeactivatingEmps} onClick={this.onClickTerminate}>{isTerminating ? 'Terminating...' : 'Terminate Company'}</button>
          </div>)}          

          <br />
        </div>
        {(showEditModal && company) && (
          <ModalEdit company={company} onClose={this.onCloseEditModal} />
        )}
      </BackstageLayout>
    );
  }

}

const stateToProps = (state) => ({
  qsId: RoutingSlx.params(state).id,
});

const dispatchToProps = (dispatch) => ({
  toastSuccess: (msg) => dispatch(ToastAx.success(msg)),
  toastError: (msg) => dispatch(ToastAx.error(msg)),
});

export default connect(stateToProps, dispatchToProps)(BackstageCompanyPage);
